import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLocale } from "@/i18n/context";
import { localizedPath } from "@/i18n/routes";
import {
  OPEN_SETTINGS_EVENT,
  getStoredConsent,
  initConsent,
  setConsent,
} from "@/lib/consent";

/**
 * Prior-consent banner for non-essential cookies (ZEKom-2, 225. člen).
 *
 * Renders nothing until the client has mounted and we know there is no stored
 * decision — the page is prerendered, so deciding on the server would bake one
 * visitor's answer into the HTML everyone gets.
 *
 * "Reject" sits at the same visual level as "Accept": under GDPR Art. 7(4) and
 * EDPB guidance, consent obtained by making refusal harder than acceptance is
 * not freely given. There is deliberately no close button that dismisses the
 * banner without a decision — dismissal is not consent, so nothing would load
 * anyway, and an X reads as "maybe".
 */
export function CookieConsent() {
  const { t, locale } = useLocale();
  const c = t.cookieConsent;

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    initConsent();
    if (!getStoredConsent()) setVisible(true);
  }, []);

  useEffect(() => {
    const open = () => {
      const stored = getStoredConsent();
      setAnalytics(stored?.analytics ?? false);
      setMarketing(stored?.marketing ?? false);
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  const decide = useCallback((choice: { analytics: boolean; marketing: boolean }) => {
    setConsent(choice);
    setVisible(false);
    setShowDetails(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={c.title}
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/95 p-4 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.25)] backdrop-blur sm:p-6"
    >
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-base font-semibold text-foreground sm:text-lg">{c.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {c.description}{" "}
          <Link
            to={localizedPath("cookies", locale)}
            className="font-medium text-primary underline underline-offset-2"
          >
            {c.cookiePolicyLink}
          </Link>{" "}
          <span aria-hidden="true">·</span>{" "}
          <Link
            to={localizedPath("privacy", locale)}
            className="font-medium text-primary underline underline-offset-2"
          >
            {c.privacyPolicyLink}
          </Link>
        </p>

        {showDetails && (
          <div className="mt-4 space-y-3 rounded-xl border border-border/60 bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">{c.necessaryTitle}</p>
                <p className="text-sm text-muted-foreground">{c.necessaryDesc}</p>
              </div>
              <Switch checked disabled aria-label={c.necessaryTitle} />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="consent-analytics" className="text-sm font-medium text-foreground">
                  {c.analyticsTitle}
                </Label>
                <p className="text-sm text-muted-foreground">{c.analyticsDesc}</p>
              </div>
              <Switch
                id="consent-analytics"
                checked={analytics}
                onCheckedChange={setAnalytics}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor="consent-marketing" className="text-sm font-medium text-foreground">
                  {c.marketingTitle}
                </Label>
                <p className="text-sm text-muted-foreground">{c.marketingDesc}</p>
              </div>
              <Switch
                id="consent-marketing"
                checked={marketing}
                onCheckedChange={setMarketing}
              />
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {!showDetails && (
            <Button
              type="button"
              variant="ghost"
              className="sm:mr-auto"
              onClick={() => setShowDetails(true)}
            >
              {c.customize}
            </Button>
          )}
          {showDetails && (
            <Button
              type="button"
              variant="ghost"
              className="sm:mr-auto"
              onClick={() => decide({ analytics, marketing })}
            >
              {c.saveSelection}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => decide({ analytics: false, marketing: false })}
          >
            {c.rejectAll}
          </Button>
          <Button type="button" onClick={() => decide({ analytics: true, marketing: true })}>
            {c.acceptAll}
          </Button>
        </div>
      </div>
    </div>
  );
}
